import React, { useState } from 'react';

import { useRecoilState } from 'recoil';
import Honeybadger from '@honeybadger-io/js';
import { useQueryClient } from '@tanstack/react-query';

import { requestApi } from 'api/Api';
import { getOwnerPractices } from 'api/Practice';

import useProcessLogin from 'lib/hooks/useProcessLogin';
import { useSmartNavigate } from 'lib/hooks/useSmartNavigate';
import { clearSelfSchedulingPersistence } from 'lib/helpers/draftCrypto';

import practiceState from 'components/practice/practiceState';
import RadioButton from 'components/shared/RadioButton/RadioButton';

import useMfaChallenge from '../lib/useMfaChallenge';

import EmailLoginForm from './EmailLoginForm';
import PhoneLoginForm from './PhoneLoginForm';
import MfaChallengeForm from './MfaChallengeForm';

const LoginForm = ({ onSuccess, extraLoginParams = {}, onPostLogin }) => {
  const [show, setShow] = useState({
    loading: false,
    confirmCode: false,
    email: true,
    selection: 'email',
    error: '',
    pwd: false
  });
  const [creds, setCreds] = useState({ email: '', pwd: '', phone: '', code: '' });
  const { navigate, navigateTo, navigateBack, locationState, currentPath } = useSmartNavigate();
  const processLogin = useProcessLogin();
  const queryClient = useQueryClient();
  const [, setOwnerPractices] = useRecoilState(practiceState.ownerPractices);
  const [, setOwnerSelectedLocation] = useRecoilState(practiceState.ownerSelectedLocation);

  const getPractices = async (user) => {
    try {
      const data = await getOwnerPractices(navigate, { practice_ids: user.practice_ids || [] });

      setOwnerSelectedLocation(user.selected_practice_id);
      // Guard the default: a business error returns no `practices`, and writing
      // `undefined` over the `[]` default would crash downstream `.map` calls.
      if (Array.isArray(data?.practices)) setOwnerPractices(data.practices);
    } catch (e) {
      // Swallowing here keeps a practice-fetch failure from aborting the rest
      // of handlePostLogin (draft clear + processLogin) as an unhandled rejection.
      Honeybadger.notify(e);
    }
  };

  const navigateToLast = (redirect) => {
    if (currentPath?.includes('/book')) return;
    // `redirect` is `res?.redirect` and can be undefined; calling .includes on
    // it would throw inside the async onSuccess chain (unhandled rejection:
    // login succeeds but no navigation, no error surfaced).
    if (!redirect) {
      Honeybadger.notify('login: missing redirect');
      return;
    }

    const hasQuery = redirect.includes('?');

    if (hasQuery) {
      window.location.replace(redirect);
      return;
    }

    if (locationState?.returnTo?.pathname?.includes(redirect)) {
      navigateBack(redirect);
    } else if (
      (locationState?.returnTo?.pathname?.includes('/myportal') ||
        locationState?.returnTo?.pathname?.includes('/book') ||
        currentPath?.includes('/book')) &&
      redirect.includes('/myportal')
    ) {
      navigateBack(currentPath?.includes('/book') ? currentPath : redirect);
    } else {
      navigateTo(redirect);
    }
  };

  const handlePostLogin = async (data, res) => {
    const { user } = data;
    if (!user) return;
    if (user.practice_ids || user.selected_practice_id) await getPractices(user);
    // A different patient logging in on the same device must not inherit the
    // prior patient's self-scheduling draft. Clear it before processLogin so
    // the wizard re-mounts in a clean state for the new identity.
    await clearSelfSchedulingPersistence({ onError: (e) => Honeybadger.notify(e) });
    await processLogin({
      user,
      redirect: res?.redirect,
      navigateToLast,
      session_token: data.session_token
    });
    // Let the host (e.g. self-scheduling) run its own post-login wiring —
    // pinning patientId into the wizard + refetching identity-scoped queries —
    // before the modal closes.
    await onPostLogin?.(user);
    onSuccess(user);
  };

  // MFA gate between password auth and post-login wiring. When the login
  // response carries mfa.required, we park the response and render the
  // challenge form; handlePostLogin runs only after /mfa/confirm succeeds.
  const mfa = useMfaChallenge({
    extraLoginParams,
    onComplete: async (data, res) => {
      queryClient.clear();
      await handlePostLogin(data, res);
    }
  });

  const emailLogin = async () => {
    const onLoginSuccess = async (data, res) => {
      if (data?.mfa?.required) {
        setShow((ps) => ({ ...ps, loading: false, error: '' }));
        mfa.start({
          kind: data.mfa.kind,
          mfaId: data.mfa.mfaId,
          pendingData: data,
          pendingRes: res
        });
        return;
      }
      queryClient.clear();
      await handlePostLogin(data, res);
    };
    const onError = (error) => {
      setShow((ps) => ({
        ...ps,
        loading: false,
        error:
          typeof error === 'string'
            ? error
            : error?.message
              ? error?.message
              : JSON.stringify(error, null, 2)
      }));
    };
    setShow((ps) => ({ ...ps, loading: true, error: '' }));
    sessionStorage.removeItem('myriad-session-reference-id');
    await requestApi({
      url: '/api/user/login',
      params: { email: creds.email, password: creds.pwd, ...extraLoginParams },
      navigate,
      onSuccess: onLoginSuccess,
      onError,
      onCatch: onError
    });
  };

  const phoneLogin = async (altCode) => {
    const onLoginSuccess = async (data, res) => {
      queryClient.clear();
      await handlePostLogin(data, res);
    };
    const onError = (error, code) => {
      if (code === 5) {
        setShow((ps) => ({ ...ps, confirmCode: false }));
      }
      setShow((ps) => ({ ...ps, loading: false }));
    };
    setShow((ps) => ({ ...ps, loading: true }));
    sessionStorage.removeItem('myriad-session-reference-id');
    await requestApi({
      url: '/api/user/mfa/confirm',
      params: { code: altCode || creds.code, ...extraLoginParams },
      navigate,
      onSuccess: onLoginSuccess,
      onError,
      onCatch: onError
    });
  };

  const phoneStart = async () => {
    const phone = creds.phone?.trim();
    if (!phone) {
      setShow((ps) => ({ ...ps, error: 'Phone number is required.' }));
      return;
    }

    setShow((ps) => ({ ...ps, loading: true }));
    sessionStorage.removeItem('myriad-session-reference-id');
    const onPhoneStartError = () => {
      setShow((ps) => ({ ...ps, loading: false }));
    };

    await requestApi({
      url: '/api/patient/checkin/phone_send',
      params: { phone },
      navigate,
      onSuccess: () => setShow((ps) => ({ ...ps, confirmCode: true, loading: false, error: '' })),
      onError: (error) => {
        setShow((ps) => ({ ...ps, error: error || 'Unable to send a verification code.' }));
        onPhoneStartError();
      },
      onCatch: onPhoneStartError,
      reportError: false
    });
    setShow((ps) => ({ ...ps, loading: false }));
  };

  if (mfa.challenge) {
    return (
      <MfaChallengeForm
        kind={mfa.challenge.kind}
        loading={mfa.loading}
        error={mfa.challenge.error}
        onSubmit={mfa.submitCode}
        onFallbackToSms={mfa.switchToSms}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-neutral-700">
        Access your health records by logging in to your account with:
      </p>
      <div className="flex w-full flex-row flex-wrap gap-2">
        <RadioButton
          wrapperClassName="w-max"
          id="modal-email-input"
          value={show.selection}
          label="User credentials"
          isSelected={show.selection === 'email'}
          onChange={() =>
            setShow((ps) => ({ ...ps, selection: 'email', email: true, phone: false }))
          }
          className="text-neutral-900"
        />
        <RadioButton
          wrapperClassName="w-max"
          id="modal-phone-input"
          value={show.selection}
          label="Mobile phone"
          isSelected={show.selection === 'phone'}
          onChange={() =>
            setShow((ps) => ({ ...ps, selection: 'phone', email: false, phone: true }))
          }
          className="text-neutral-900"
        />
      </div>
      <div>
        {show.email ? (
          <EmailLoginForm
            creds={creds}
            setCreds={setCreds}
            show={show}
            setShow={setShow}
            onSubmit={emailLogin}
          />
        ) : (
          <PhoneLoginForm
            creds={creds}
            setCreds={setCreds}
            show={show}
            onSendCode={phoneStart}
            onConfirmCode={phoneLogin}
          />
        )}
      </div>
    </div>
  );
};

export default LoginForm;
